import React, { useState } from 'react';
import axios from 'axios';
import { APP_API_URL } from '../../utils';
import { useSelector } from 'react-redux';
import UIDropdown from '../../components/ui/ui-dropdown';
import Swal from 'sweetalert2';
import DuoModal from '../../components/ui/duo-modal';

export default function Add({ open, onClose, refreshData, storeData, posteData }) {
  const user = useSelector((state) => state.auth.user);

  const getCurrentDate = () => new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    poste_id: '',
    telephone: '',
    date_embauche: getCurrentDate(),
    statut: 'actif',
    store_id: '',
    description: '',
    user_id: user?.id,
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [selectedStore, setSelectedStore] = useState({ label: 'Sélectionner', value: '' });
  const [selectedPoste, setSelectedPoste] = useState({ label: 'Sélectionner', value: '' });
  const [showPassword, setShowPassword] = useState(false);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleStoreSelect = (option) => {
    setSelectedStore(option);
    setFormData((prev) => ({ ...prev, store_id: option.value }));
  };

  const handlePosteSelect = (option) => {
    setSelectedPoste(option);
    setFormData((prev) => ({ ...prev, poste_id: option.value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      poste_id: '',
      telephone: '',
      date_embauche: getCurrentDate(),
      statut: 'actif',
      store_id: '',
      description: '',
      user_id: user?.id,
      password: '',
    });
    setSelectedStore({ label: 'Sélectionner', value: '' });
    setSelectedPoste({ label: 'Sélectionner', value: '' });
    setShowPassword(false);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${APP_API_URL}/personnels`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: response.data.message || 'Personnel ajouté avec succès !',
        timer: 2000,
        showConfirmButton: false,
      });

      resetForm();
      onClose();
      refreshData();
    } catch (error) {
      if (error.response?.status === 422) {
        setErrors(error.response.data.errors);
        Swal.fire({
          icon: 'error',
          title: 'Erreur de validation',
          text: 'Certains champs contiennent des erreurs. Veuillez les corriger.',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: "Une erreur est survenue lors de l'ajout du personnel. Veuillez réessayer.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DuoModal
      title="Ajouter un personnel"
      size="lg"
      onClose={handleClose}
      footer={
        <>
          <button type="button" className="duo-btn duo-btn--ghost" onClick={handleClose} disabled={isLoading}>
            Annuler
          </button>
          <button type="submit" form="add-personnel-form" className="duo-btn duo-btn--primary" disabled={isLoading}>
            {isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </>
      }
    >
      <form id="add-personnel-form" onSubmit={handleSubmit}>
        <div className="row g-2">
          <div className="col-md-6">
            <div className="duo-form-group">
              <label htmlFor="add-personnel-name" className="duo-form-label">
                Nom du personnel
              </label>
              <input
                type="text"
                className="duo-input"
                id="add-personnel-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && <p className="text-danger small mt-1 mb-0">{errors.name[0]}</p>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="duo-form-group">
              <label htmlFor="add-personnel-email" className="duo-form-label">
                Email
              </label>
              <input
                type="email"
                className="duo-input"
                id="add-personnel-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <p className="text-danger small mt-1 mb-0">{errors.email[0]}</p>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="duo-form-group">
              <label htmlFor="add-personnel-telephone" className="duo-form-label">
                Téléphone
              </label>
              <input
                type="text"
                className="duo-input"
                id="add-personnel-telephone"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
              />
              {errors.telephone && <p className="text-danger small mt-1 mb-0">{errors.telephone[0]}</p>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="duo-form-group">
              <label className="duo-form-label">Choisir un poste</label>
              <UIDropdown
                options={posteData.map((item) => ({ label: item.titre, value: item.id }))}
                selectedOption={selectedPoste}
                onSelect={handlePosteSelect}
                enableSearch={true}
              />
              {errors.poste_id && <p className="text-danger small mt-1 mb-0">{errors.poste_id[0]}</p>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="duo-form-group">
              <label className="duo-form-label">Choisir une boutique</label>
              <UIDropdown
                options={storeData.map((item) => ({ label: item.name, value: item.id }))}
                selectedOption={selectedStore}
                onSelect={handleStoreSelect}
                enableSearch={true}
              />
              {errors.store_id && <p className="text-danger small mt-1 mb-0">{errors.store_id[0]}</p>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="duo-form-group">
              <label htmlFor="add-personnel-password" className="duo-form-label">
                Mot de passe
              </label>
              <div className="d-flex gap-2">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="duo-input"
                  id="add-personnel-password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="duo-btn duo-btn--ghost"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 'Masquer' : 'Afficher'}
                </button>
              </div>
              {errors.password && <p className="text-danger small mt-1 mb-0">{errors.password[0]}</p>}
            </div>
          </div>
          <div className="col-12">
            <div className="duo-form-group">
              <label htmlFor="add-personnel-description" className="duo-form-label">
                Description
              </label>
              <textarea
                className="duo-input"
                id="add-personnel-description"
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
              />
              {errors.description && <p className="text-danger small mt-1 mb-0">{errors.description[0]}</p>}
            </div>
          </div>
          <div className="col-12">
            <div className="duo-form-group mb-0">
              <label className="duo-form-label d-flex align-items-start gap-2">
                <input
                  type="checkbox"
                  className="form-check-input mt-1"
                  id="add-personnel-statut"
                  name="statut"
                  checked={formData.statut === 'actif'}
                  onChange={(e) =>
                    handleChange({ target: { name: 'statut', value: e.target.checked ? 'actif' : 'inactif' } })
                  }
                />
                <span>
                  Statut actif
                  <span className="d-block fw-normal" style={{ fontSize: '0.78rem', color: '#777' }}>
                    Décochez cette case si vous souhaitez que ce personnel soit inactif.
                  </span>
                </span>
              </label>
              {errors.statut && <p className="text-danger small mt-1 mb-0">{errors.statut[0]}</p>}
            </div>
          </div>
        </div>
      </form>
    </DuoModal>
  );
}
